import { ReactNode, useState } from 'react';

import { cx } from '@libs/classnames';
import { CollapseIcon } from '@ui/components/CollapseIcon';
import { Loader } from '@ui/components/Loader';
import {
  FancyModal,
  FancyModalPanel,
  FancyPanelAction,
  FancyPanelTitle,
} from '@ui/components/panels/FancyPanel';
import { Plural } from '@ui/components/Plural';
import { ThemeButton } from '@ui/components/ThemeButton';
import { CameraIcon } from '@ui/icons/CameraIcon';
import { PlusIcon } from '@ui/icons/PlusIcon';

import { ThemeIconButton } from '@features/shared/ui/ThemeIconButton';
import { roomKey } from '@features/video/lib/ticket';
import { RoomRef } from '@features/video/types';

import styles from './RoomPickerModal.module.css';

export type PickerParticipant = { userId: string; name: string };

export type PickerRoom = {
  number: number;
  participants: PickerParticipant[];
};

// Турнир в выборе: идет или начнется в ближайший час, видеоконтроль включен. Комнаты и
// участники уже загружены: список появляется целиком, без построчной подгрузки
export type PickerTournament = {
  tournamentId: string;
  title: string;
  rooms: PickerRoom[];
  // не пришли: участники без комнаты
  absent?: PickerParticipant[];
};

export type Props = {
  // в порядке показа: турнир основной комнаты первым
  tournaments: PickerTournament[];
  isLoading?: boolean;
  // основная комната — путь страницы: ее не убрать, вместо кнопки пометка «Основная»
  main: RoomRef;
  // комнаты, уже собранные в мультикомнату
  selected: RoomRef[];
  // вариант для выбора: «Отсутствуют» строкой как обычная комната, не приглушенной
  absentAsRoom?: boolean;
  onToggle: (room: RoomRef) => void;
  onApply: () => void;
  onClose: () => void;
};

// Присоединение комнат к мультикомнате: три уровня — турниры клуба, которые идут или начнутся
// в ближайший час, заголовками без сворачивания, под каждым строки комнат с квадратной
// кнопкой «+» (как у участника) или «Убрать» как в модалке участников, участники комнаты
// раскрываются нумерованным списком
export function RoomPickerModal(props: Props) {
  return (
    <FancyModal
      className={styles.panel}
      contentClassName={styles.content}
      actions={<Actions {...props} />}
      onClose={props.onClose}
    >
      <Picker {...props} />
    </FancyModal>
  );
}

// Сама панель без оверлея: для сторибука
export function RoomPickerPanel(props: Props) {
  return (
    <FancyModalPanel
      className={styles.panel}
      contentClassName={styles.content}
      actions={<Actions {...props} />}
      onClose={props.onClose}
    >
      <Picker {...props} />
    </FancyModalPanel>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Одно действие с цветным акцентом; закрытие — крестик панели
function Actions({ selected, onApply }: Props) {
  return (
    <FancyPanelAction className={styles.apply} disabled={selected.length === 0} onClick={onApply}>
      Применить
    </FancyPanelAction>
  );
}

function Picker({ tournaments, isLoading, main, selected, absentAsRoom, onToggle }: Props) {
  const mainKey = roomKey(main);
  const selectedKeys = new Set(selected.map(roomKey));
  const [open, flip] = useOpenKeys();
  return (
    <>
      <FancyPanelTitle>Присоединить другие комнаты</FancyPanelTitle>
      <p className={styles.hint}>
        После присоединения включится режим мультикомнаты, когда несколько комнат отображаются как
        один общий зал
      </p>

      {isLoading && <Loader preset="mobile" centered ignoreClubColors />}
      {!isLoading && !tournaments.length && (
        <p className={styles.empty}>
          Сейчас нет турниров, которые идут или начнутся в ближайший час
        </p>
      )}

      <ul className={styles.tournaments}>
        {tournaments.map((t) => (
          <li key={t.tournamentId} className={styles.tournament}>
            <div className={styles.tournament__title}>{t.title}</div>
            {!t.rooms.length && <p className={styles.note}>Комнаты еще не созданы</p>}
            {t.rooms.length > 0 && (
              <ul className={styles.rooms}>
                {t.rooms.map((room) => {
                  const ref = { tournamentId: t.tournamentId, roomNumber: room.number };
                  const key = roomKey(ref);
                  return (
                    <Row
                      key={key}
                      label={
                        <>
                          <CameraIcon className={styles.row__camera} />
                          Комната {room.number}
                          <span className={styles.meta}>
                            · <Participants count={room.participants.length} />
                          </span>
                        </>
                      }
                      participants={room.participants}
                      isOpen={open.has(key)}
                      onFlip={() => flip(key)}
                      action={
                        key === mainKey ? (
                          <ThemeButton className={cx(styles.row__button, styles.is_main)}>
                            Основная
                          </ThemeButton>
                        ) : selectedKeys.has(key) ? (
                          <ThemeButton
                            className={styles.row__button}
                            inverted
                            onClick={() => onToggle(ref)}
                          >
                            Убрать
                          </ThemeButton>
                        ) : (
                          <ThemeIconButton
                            className={styles.row__add}
                            onClick={() => onToggle(ref)}
                          >
                            <PlusIcon className={styles.row__plus} />
                          </ThemeIconButton>
                        )
                      }
                    />
                  );
                })}
                {t.absent && t.absent.length > 0 && (
                  <Row
                    muted={!absentAsRoom}
                    label={
                      <>
                        Отсутствуют
                        <span className={styles.meta}>
                          · <Participants count={t.absent.length} />
                        </span>
                      </>
                    }
                    participants={t.absent}
                    isOpen={open.has(`${t.tournamentId}:absent`)}
                    onFlip={() => flip(`${t.tournamentId}:absent`)}
                  />
                )}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

// Строка комнаты: стрелка раскрытия, подпись, справа кнопка; раскрытые участники —
// нумерованный список под подписью
function Row({
  label,
  muted,
  participants,
  isOpen,
  onFlip,
  action,
}: {
  label: ReactNode;
  muted?: boolean;
  participants: PickerParticipant[];
  isOpen: boolean;
  onFlip: () => void;
  action?: ReactNode;
}) {
  const canOpen = participants.length > 0;
  return (
    <li className={cx(styles.row, { [styles.is_muted]: muted })}>
      <div className={styles.row__body}>
        <button type="button" className={styles.row__toggle} disabled={!canOpen} onClick={onFlip}>
          <span className={styles.row__icon}>
            {canOpen && <CollapseIcon className={styles.row__collapse} toggled={isOpen} />}
          </span>
          <span className={styles.row__label}>{label}</span>
        </button>
        {isOpen && (
          <ol className={styles.row__participants}>
            {participants.map((p) => (
              <li key={p.userId}>{p.name}</li>
            ))}
          </ol>
        )}
      </div>
      {action}
    </li>
  );
}

function Participants({ count }: { count: number }) {
  return (
    <>
      {count} <Plural cnt={count} one="участник" few="участника" many="участников" />
    </>
  );
}

// Раскрытые списки участников по ключу комнаты
function useOpenKeys(): [ReadonlySet<string>, (key: string) => void] {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const flip = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  return [open, flip];
}
